import type { I18n } from '@learnforge/i18n';

export interface TopbarSelectorOption<T extends string> {
  value: T;
  labelKey: string;
}

export interface TopbarSelector<T extends string> {
  options: ReadonlyArray<TopbarSelectorOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Optional i18n key used for the select's aria-label. */
  ariaLabelKey?: string;
}

export interface MountTopbarOptions<T extends string = string> {
  eyebrowKey: string;
  /** Static title text. Used when `selector` is not provided. */
  titleKey?: string;
  /** Dropdown for switching between scenes/figures/experiments. Replaces the static title. */
  selector?: TopbarSelector<T>;
  i18n: I18n<string>;
}

export interface TopbarHandle<T extends string = string> {
  setTitleKey(key: string): void;
  setSelectorValue(value: T): void;
  destroy(): void;
}

export function mountTopbar<T extends string = string>(
  el: HTMLElement,
  opts: MountTopbarOptions<T>,
): TopbarHandle<T> {
  el.classList.add('topbar');

  const hasSelector = opts.selector !== undefined;
  const titleHTML = hasSelector
    ? `<select class="title title-select" aria-label=""></select>`
    : `<div class="title"></div>`;
  el.innerHTML = `<div class="eyebrow"></div>${titleHTML}`;

  const eyebrow = el.querySelector('.eyebrow') as HTMLDivElement;

  let currentTitleKey = opts.titleKey ?? '';

  const renderEyebrow = (): void => {
    eyebrow.textContent = opts.i18n.t(opts.eyebrowKey);
  };

  let renderTitle: () => void;
  let onChange: (() => void) | null = null;
  let select: HTMLSelectElement | null = null;

  if (hasSelector && opts.selector) {
    select = el.querySelector('select.title-select') as HTMLSelectElement;
    for (const o of opts.selector.options) {
      const optEl = document.createElement('option');
      optEl.value = o.value;
      optEl.dataset['labelKey'] = o.labelKey;
      optEl.textContent = opts.i18n.t(o.labelKey);
      select.appendChild(optEl);
    }
    select.value = opts.selector.value;
    if (opts.selector.ariaLabelKey) {
      select.setAttribute('aria-label', opts.i18n.t(opts.selector.ariaLabelKey));
    }
    const selectorOnChange = opts.selector.onChange;
    onChange = (): void => {
      selectorOnChange(select!.value as T);
    };
    select.addEventListener('change', onChange);

    renderTitle = (): void => {
      for (const opt of Array.from(select!.options)) {
        const key = opt.dataset['labelKey'];
        if (key) opt.textContent = opts.i18n.t(key);
      }
      if (opts.selector?.ariaLabelKey) {
        select!.setAttribute('aria-label', opts.i18n.t(opts.selector.ariaLabelKey));
      }
    };
  } else {
    const titleEl = el.querySelector('.title') as HTMLDivElement;
    renderTitle = (): void => {
      titleEl.textContent = currentTitleKey ? opts.i18n.t(currentTitleKey) : '';
    };
  }

  renderEyebrow();
  renderTitle();
  const unsubscribe = opts.i18n.subscribe(() => {
    renderEyebrow();
    renderTitle();
  });

  return {
    setTitleKey(key): void {
      currentTitleKey = key;
      if (!hasSelector) {
        renderTitle();
      }
    },
    setSelectorValue(value): void {
      if (select) select.value = value;
    },
    destroy(): void {
      unsubscribe();
      if (select && onChange) select.removeEventListener('change', onChange);
    },
  };
}
